import { useEffect, useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  Box,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  fstLotteryProducts,
  lotteryFailureModes,
  oemVendors,
  productLabelMap,
  solutions,
  status,
} from '../../../../../data/forms/depot/benchRepairDropdownOptions.js';
import { yesNoOptions } from '../../../../../data/forms/generalDropdownOptions.js';
import { depotTimeWorked } from '../../../../../data/forms/depot/depotDropdownOptions.js';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import { createFilterOptions } from '@mui/material/Autocomplete';

const partsUsedFilterOptions = createFilterOptions({
  matchFrom: 'any',
  trim: true,
  stringify: option => String(option ?? '').trim(),
});

const AdditionalItemsForm = ({
  mainForm,
  entries,
  setEntries,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [partsUsedOptions, setPartsUsedOptions] = useState([]);

  useEffect(() => {
    setPartsUsedOptions([]); // Clear options before setting new ones to avoid showing stale options if this is a re-fetch.
    async function loadPartsUsedOptions() {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT_FORMS}/depot/benchrepair/itemNumbers`,
          { params: { product: 'partsBenchRepair' } },
        );
        const options = Array.isArray(data)
          ? data.map(itemNumber => String(itemNumber ?? '').trim()).filter(Boolean)
          : [];

        // API already orders with "None" first; keep UI behavior consistent.
        setPartsUsedOptions(options);
      } catch (err) {
        console.error('Failed to load parts used options from API:', err);
        setSnackbarMessage(
          'Failed to load Parts Used options. Please try again by refreshing the page. If the issue persists, contact support.',
        );
        setSnackbarSeverity('error');
        setIsSnackbarOpen(true);
        setPartsUsedOptions([]);
      }
    }
    loadPartsUsedOptions();
  }, []);

  const handleFieldChange = (indexOfEntry, event) => {
    const { name, value } = event.target;
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[indexOfEntry] = {
        ...updatedEntries[indexOfEntry],
        [name]: value,
      };
      return updatedEntries;
    });

    if (name === 'isRepeatReturn' && value === 'No') {
      setEntries(prevEntries => {
        const updatedEntries = [...prevEntries];
        updatedEntries[indexOfEntry] = {
          ...updatedEntries[indexOfEntry],
          returnDate: null,
        };
        return updatedEntries;
      });
    }
  };

  // Product dropdown → GET item numbers for that category, then fill the Item Number <Select>.
  const handleProductFieldChange = async (indexOfEntry, event) => {
    const selectedProduct = event.target.value;
    const categoryKey = productLabelMap[selectedProduct];

    // Clear prior item selection and options immediately so the UI does not show stale part numbers.
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[indexOfEntry] = {
        ...updatedEntries[indexOfEntry],
        product: selectedProduct,
        itemNumber: '',
        itemNumberOptions: [],
      };
      return updatedEntries;
    });

    let itemNumberOptions = [];

    if (categoryKey) {
      try {
        const { data } = await axios.get(
          `${
            process.env.REACT_APP_ENDPOINT_FORMS
          }/depot/benchrepair/itemNumbers`,
          { params: { product: categoryKey } },
        );
        itemNumberOptions = data;
      } catch (err) {
        console.error('Failed to load item numbers from API:', err);
        setSnackbarMessage(
          'Failed to load Item Number options. Please try again by refreshing the page. If the issue persists, contact support.',
        );
        setSnackbarSeverity('error');
        setIsSnackbarOpen(true);
      }
    }

    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[indexOfEntry] = {
        ...updatedEntries[indexOfEntry],
        itemNumberOptions,
      };
      return updatedEntries;
    });
  };

  const handleReturnDateChange = (value, index) => {
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        returnDate: value,
      };
      return updatedEntries;
    });
  };

  const handleStartDateChange = (value, index) => {
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        startDate: value,
      };
      return updatedEntries;
    });
  };

  const handleCompletionDateChange = (value, index) => {
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        completionDate: value,
      };
      return updatedEntries;
    });
  };

  const handleAddEntry = () => {
    setEntries(prevEntries => [
      ...prevEntries,
      {
        id: prevEntries.length + 1,
        product: '',
        itemNumber: '',
        itemNumberOptions: [],
        rmaNumber: '',
        assetTag: '',
        serialNumber: '',
        resolution: '',
        failureMode: '',
        solution: [],
        partsUsed: [],
        isRepeatReturn: '',
        returnDate: null,
        oemVendor: '',
        totalWorkTime: '',
        status: '',
        startDate: null,
        completionDate: null,
        comments: '',
      },
    ]);
  };

  const handleRemoveItem = indexToRemove => {
    setEntries(prevEntries =>
      prevEntries.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleDuplicateItem = indexToDuplicate => {
    setEntries(prevEntries => {
      const itemToDuplicate = prevEntries[indexToDuplicate];
      const newItem = { ...itemToDuplicate, id: prevEntries.length + 1 };
      return [...prevEntries, newItem];
    });
  };

  const handleAutocompleteChange = (index, value, name) => {
    setEntries(prevEntries => {
      const updatedEntries = [...prevEntries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        [name]: value,
      };
      return updatedEntries;
    });
  };

  return (
    <Box>
      {entries.length < 25 && (
        <Button
          variant='contained'
          sx={{
            marginRight: '10px',
            backgroundColor: 'var(--procyon-green)',
            color: '#000000',
            fontFamily: 'BrightstarSans-Regular',
            marginBottom: '10px',
          }}
          onClick={handleAddEntry}
        >
          + Additional Item
        </Button>
      )}
      <Grid container spacing={2}>
        {entries.map((fields, index) => (
          <Box sx={{ width: { md: '28vw' } }} key={fields.id ?? index}>
            <Grid
              container
              sx={{
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant='h6'
                sx={{ fontFamily: 'BrightstarSans-Regular' }}
              >
                Item: {index + 1}
              </Typography>
              <div>
                {entries.length < 25 && (
                  <Button
                    color='secondary'
                    size='small'
                    onClick={() => handleDuplicateItem(index)}
                    sx={{
                      fontFamily: 'BrightstarSans-Regular',
                    }}
                  >
                    Duplicate
                  </Button>
                )}
                {entries.length > 1 && (
                  <Button
                    color='error'
                    size='small'
                    onClick={() => handleRemoveItem(index)}
                    sx={{
                      fontFamily: 'BrightstarSans-Regular',
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FormControl
                  fullWidth
                  required
                  sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                >
                  <InputLabel>Product</InputLabel>
                  <Select
                    name='product'
                    label='Product'
                    value={fields.product}
                    onChange={e => handleProductFieldChange(index, e)}
                  >
                    {fstLotteryProducts &&
                      fstLotteryProducts.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Tooltip
                  title={
                    mainForm.jurisdiction !== 'RRF'
                      ? 'Only applicable for RRF jurisdiction'
                      : ''
                  }
                  arrow
                >
                  <FormControl
                    required={mainForm.jurisdiction === 'RRF'}
                    disabled={mainForm.jurisdiction !== 'RRF'}
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                    }}
                    fullWidth
                  >
                    <InputLabel>{'Repeat Return'}</InputLabel>
                    <Select
                      value={fields.isRepeatReturn}
                      label={'Repeat Return'}
                      onChange={e => handleFieldChange(index, e)}
                      name='isRepeatReturn'
                    >
                      {yesNoOptions.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
                <TextField
                  fullWidth
                  name='assetTag'
                  label='Asset Tag'
                  value={fields.assetTag}
                  onChange={e => handleFieldChange(index, e)}
                  sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                />
                <FormControl
                  fullWidth
                  required
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                  }}
                >
                  <InputLabel>Resolution</InputLabel>
                  <Select
                    name='resolution'
                    label='Resolution'
                    value={fields.resolution}
                    onChange={e => handleFieldChange(index, e)}
                    sx={{ backgroundColor: '#ffffff' }}
                    required
                  >
                    {yesNoOptions &&
                      yesNoOptions.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControl
                  required
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                    width: '100%',
                  }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    label={'Status'}
                    name='status'
                    value={fields.status}
                    onChange={e => handleFieldChange(index, e)}
                  >
                    {status.map(value => {
                      return <MenuItem value={value}>{value}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='Start Date*'
                    value={fields.startDate}
                    onChange={value => handleStartDateChange(value, index)}
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                      width: '100%',
                    }}
                    required
                  />
                </LocalizationProvider>
                <Autocomplete
                  id={`solution-autocomplete-${fields.id ?? index}`}
                  multiple
                  disablePortal
                  options={solutions}
                  disableCloseOnSelect
                  value={fields.solution}
                  getOptionLabel={option => option}
                  onChange={(e, val) =>
                    handleAutocompleteChange(index, val, 'solution')
                  }
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                    width: '100%',
                  }}
                  renderInput={params => (
                    <TextField {...params} name='solution' label='Solution' />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                          checkedIcon={<CheckBoxIcon fontSize='small' />}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    );
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Tooltip
                  title={
                    fields.product
                      ? ''
                      : fields.itemNumberOptions.length === 0
                        ? 'No Item Number options available'
                        : 'Select Product first to see Item Numbers'
                  }
                  arrow
                >
                  <FormControl
                    required
                    fullWidth
                    disabled={
                      !fields.product || fields.itemNumberOptions.length === 0
                    }
                    sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                  >
                    <InputLabel>
                      {fields.product
                        ? `Item Number (${fields.product})`
                        : 'Item Number'}
                    </InputLabel>
                    <Select
                      name='itemNumber'
                      label={
                        fields.product
                          ? `Item Number (${fields.product})`
                          : 'Item Number'
                      }
                      value={fields.itemNumber}
                      onChange={e => handleFieldChange(index, e)}
                      sx={{ backgroundColor: '#ffffff' }}
                    >
                      {fields.itemNumberOptions.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
                <Tooltip
                  title={
                    fields.isRepeatReturn !== 'Yes' ||
                    mainForm.jurisdiction !== 'RRF'
                      ? "Please select 'Yes' on Repeat Return to enable this field"
                      : ''
                  }
                  arrow
                >
                  <span>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        disabled={fields.isRepeatReturn !== 'Yes'}
                        label={
                          fields.isRepeatReturn === 'Yes'
                            ? 'Return Date*'
                            : 'Return Date'
                        }
                        value={fields.returnDate}
                        onChange={value => handleReturnDateChange(value, index)}
                        sx={{
                          backgroundColor: '#ffffff',
                          marginBottom: '15px',
                          width: '100%',
                        }}
                        required={fields.isRepeatReturn === 'Yes'}
                      />
                    </LocalizationProvider>
                  </span>
                </Tooltip>
                <TextField
                  required
                  fullWidth
                  name='serialNumber'
                  label='Serial Number'
                  value={fields.serialNumber}
                  onChange={e => handleFieldChange(index, e)}
                  sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                />
                <FormControl
                  required
                  fullWidth
                  sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
                >
                  <InputLabel>Failure Mode</InputLabel>
                  <Select
                    name='failureMode'
                    label='Failure Mode'
                    value={fields.failureMode}
                    onChange={e => handleFieldChange(index, e)}
                    required
                  >
                    {lotteryFailureModes &&
                      lotteryFailureModes.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Tooltip title={'Total Work Time is in Minutes'}>
                  <FormControl
                    required
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                      width: '100%',
                    }}
                  >
                    <InputLabel>{'Total Work Time'}</InputLabel>
                    <Select
                      value={fields.totalWorkTime}
                      label={'Total Work Time'}
                      onChange={e => handleFieldChange(index, e)}
                      name='totalWorkTime'
                    >
                      {depotTimeWorked.map(option => (
                        <MenuItem value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
                <Tooltip
                  title={
                    mainForm.oem !== 'Yes'
                      ? "Please select 'Yes' on OEM to enable this field"
                      : ''
                  }
                  arrow
                >
                  <FormControl
                    required={mainForm.oem === 'Yes'}
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                      width: '100%',
                    }}
                    disabled={mainForm.oem !== 'Yes'}
                  >
                    <InputLabel>{'Vendor'}</InputLabel>
                    <Select
                      value={fields.oemVendor}
                      label={'Vendor'}
                      onChange={e => handleFieldChange(index, e)}
                      name='oemVendor'
                    >
                      {oemVendors.map(option => (
                        <MenuItem value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Tooltip
                    title={
                      fields.startDate
                        ? ''
                        : 'Please select Start Date to enable this field'
                    }
                    arrow
                  >
                    <span>
                      <DatePicker
                        label='Completion Date'
                        value={fields.completionDate}
                        minDate={fields.startDate}
                        disabled={fields.startDate ? false : true}
                        onChange={value =>
                          handleCompletionDateChange(value, index)
                        }
                        sx={{
                          backgroundColor: '#ffffff',
                          marginBottom: '15px',
                          width: '100%',
                        }}
                      />
                    </span>
                  </Tooltip>
                </LocalizationProvider>
                {/* <Tooltip
                  title={
                    partsUsedOptions.length > 0
                      ? ''
                      : 'No Parts Used options available.'
                  }
                  arrow
                > */}
                <Autocomplete
                  id={`parts-used-autocomplete-${fields.id ?? index}`}
                  multiple
                  disablePortal
                  // disabled={partsUsedOptions.length === 0}
                  options={partsUsedOptions}
                  disableCloseOnSelect
                  getOptionLabel={option => String(option ?? '')}
                  isOptionEqualToValue={(option, value) =>
                    String(option ?? '').trim() === String(value ?? '').trim()
                  }
                  filterOptions={partsUsedFilterOptions}
                  value={fields.partsUsed}
                  onChange={(e, val) =>
                    handleAutocompleteChange(index, val, 'partsUsed')
                  }
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                    width: '100%',
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name='partsUsed'
                      label='Parts Used'
                      required
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                          checkedIcon={<CheckBoxIcon fontSize='small' />}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    );
                  }}
                />
                {/* </Tooltip> */}
              </Grid>
            </Grid>
            <TextField
              label='Comments'
              name='comments'
              value={fields.comments}
              onChange={e => handleFieldChange(index, e)}
              multiline
              rows={4.1}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </Box>
        ))}
      </Grid>
    </Box>
  );
};

export default AdditionalItemsForm;
